/**
 * idlToDts.js
 *
 * Converts Platform IDL / PlatformFunctionSignature objects to TypeScript
 * declaration strings for injection into Monaco via addExtraLib.
 *
 * IDL type nodes in JSON have this shape:
 *   { type: "string" | "number" | "boolean" | "object" | "array" | "any" | "<NamedType>",
 *     required?: boolean,
 *     fields?: { [key]: IDLTypeNode },   // when type === "object"
 *     items?: IDLTypeNode }              // when type === "array"
 *
 * PlatformFunctionSignature:
 *   { input?: { [param]: IDLTypeNode }, output?: IDLTypeNode }
 */

/**
 * Converts a single IDL type node to a TypeScript type string.
 * @param {Object} node
 * @returns {string}
 */
function idlTypeNodeToTs(node) {
    if (!node) return 'any'
    const type = typeof node.type === 'string' ? node.type : 'any'
    switch (type) {
        case 'string':  return 'string'
        case 'number':  return 'number'
        case 'boolean': return 'boolean'
        case 'array':
            return node.items ? `Array<${idlTypeNodeToTs(node.items)}>` : 'any[]'
        case 'object':
            if (node.fields && Object.keys(node.fields).length > 0) {
                return objectShape(node.fields)
            }
            return 'Record<string, any>'
        case 'any':
            return 'any'
        case 'relation':
            // Standalone usage — the stored value is a UUID string.
            return 'string'
        default:
            // Named type reference — emit as-is (caller is responsible for declaring it)
            return type
    }
}

/**
 * Converts a map of IDL type nodes (fields) to a TypeScript inline object type.
 * @param {Object} fields  { [key]: IDLTypeNode }
 * @returns {string}  e.g. "{ name: string; age?: number }"
 */
function objectShape(fields) {
    const props = Object.entries(fields)
        .flatMap(([key, node]) => {
            const opt = node.required ? '' : '?'
            if (node.type === 'relation') {
                // Relation fields expand to two properties: UUID + hydrated _RefObj.
                const itemTs = node.items ? idlTypeNodeToTs(node.items) : 'any'
                const refType = node.cardinality === 'many' ? `Array<${itemTs}>` : itemTs
                return [
                    `${key}${opt}: string`,
                    `${key}_RefObj?: ${refType}`
                ]
            }
            return [`${key}${opt}: ${idlTypeNodeToTs(node)}`]
        })
        .join('; ')
    return `{ ${props} }`
}

/**
 * Converts a PlatformFunctionSignature to the value type of a
 * PlatformFunction entry: "{ input: <T>; output: <T> }" or null.
 * @param {Object|null} signature
 * @returns {string|null}
 */
function signatureToTs(signature) {
    if (!signature) return null
    const parts = []
    if (signature.input && Object.keys(signature.input).length > 0) {
        parts.push(`input: ${objectShape(signature.input)}`)
    }
    if (signature.output) {
        parts.push(`output: ${idlTypeNodeToTs(signature.output)}`)
    }
    if (parts.length === 0) return null
    return `{ ${parts.join('; ')} }`
}

/**
 * Generates an "interface PlatformFunctions { ... }" declaration for
 * a list of platform functions. Each function should expose its active-version
 * signature as { name: string, signature: PlatformFunctionSignature|null }.
 *
 * Callers should map the raw function list to this shape, picking the
 * appropriate version (dev/live) before calling this function.
 *
 * @param {Array<{ name: string, signature: Object|null }>} functions
 * @returns {string}  TypeScript declaration snippet, or "" if nothing to emit
 */
/**
 * Converts an EntityIDL's named type definitions to top-level `declare type` strings.
 * Named types appear as "MyType" references in IDLTypeNode.type; we resolve them
 * so Monaco can inline them in field tooltips.
 *
 * @param {Object|null} types  EntityIDL.types map
 * @returns {string[]}
 */
function typesBlockLines(types) {
    if (!types || Object.keys(types).length === 0) return []
    return Object.entries(types).map(([name, def]) => {
        switch (def.kind) {
            case 'object':
                if (def.fields && Object.keys(def.fields).length > 0)
                    return `declare type ${name} = ${objectShape(def.fields)};`
                return `declare type ${name} = Record<string, any>;`
            case 'enum':
                if (def.values && def.values.length > 0)
                    return `declare type ${name} = ${def.values.map(v => JSON.stringify(v)).join(' | ')};`
                return `declare type ${name} = string;`
            case 'alias':
                return `declare type ${name} = ${def.type ? String(def.type.primitive || def.type.defined || 'any') : 'any'};`
            case 'array':
                return `declare type ${name} = ${def.items ? `Array<${idlTypeNodeToTs(def.items)}>` : 'any[]'};`
            default:
                return `declare type ${name} = any;`
        }
    })
}

/**
 * Converts an EntityIDL.fields map to an inline object type string.
 * @param {Object|null} fields
 * @returns {string}
 */
function entityFieldsToTs(fields) {
    if (!fields || Object.keys(fields).length === 0) return 'Record<string, any>'
    return objectShape(fields)
}

/**
 * Converts the components map from GET /v1/system/idl into a ComponentSchemas
 * interface declaration for declaration-merging into Monaco.
 *
 * components: Record<string, EntityIDL>
 *   keys: "namespace::name" (base IDL) or "namespace::name/extensionName" (extension IDL)
 *
 * Emits:
 *   - named type declarations from each IDL's `types` map
 *   - a global `interface ComponentSchemas { "ref": { ...fields } }` block
 *
 * @param {Object} components
 * @returns {string}
 */
/**
 * Converts the components map from GET /v1/system/idl into two declaration-
 * merge interfaces for Monaco:
 *
 *   interface ComponentModels    { "ref": { field: type; ... } }
 *   interface ComponentFunctions { "ref": { "fnName": { input: T; output: T } } }
 *
 * Each key in `components` is a bare component ref ("namespace::name").
 * Each value is a ComponentIDLEntry:
 *   { schemas: { "default": { types, fields }, ... }, functions: [...], extensions: { "extName": { schemas, functions } } }
 *
 * ComponentModels keys:
 *   - Default schema → "namespace::name"
 *   - Named schemas  → "namespace::name.schema"
 *   - Extension default → "namespace::name/extName"
 *   - Extension named schema → "namespace::name.schema/extName"
 *
 * ComponentFunctions keys:
 *   - "namespace::name" → all functions (base + all extension functions merged)
 *
 * @param {Object} components  Record<string, ComponentIDLEntry>
 * @returns {{ modelsDts: string, functionsDts: string }}
 */
export function componentIDLsToDts(components) {
    if (!components || Object.keys(components).length === 0) return { modelsDts: '', functionsDts: '', typesDts: '' }

    const modelEntries = []
    const fnEntries = []
    // Merged type map: global comp types first, then per-schema types overlay.
    // Extension global/schema types are also collected here.
    // Schema-level types win over global; last writer wins within same precedence tier.
    const allTypes = {}

    for (const [ref, comp] of Object.entries(components)) {
        if (!comp) continue

        // ── Named types ───────────────────────────────────────────────────────
        // 1. Global component types (lowest precedence).
        Object.assign(allTypes, comp.types || {})
        // 2. Per-schema types override globals.
        for (const schemaIDL of Object.values(comp.schemas || {})) {
            if (schemaIDL) Object.assign(allTypes, schemaIDL.types || {})
        }
        // 3. Extension global types.
        const extensions = comp.extensions || {}
        for (const ext of Object.values(extensions)) {
            if (!ext) continue
            Object.assign(allTypes, ext.types || {})
            // 4. Extension per-schema types.
            for (const extSchemaIDL of Object.values(ext.schemas || {})) {
                if (extSchemaIDL) Object.assign(allTypes, extSchemaIDL.types || {})
            }
        }

        // ── ComponentModels ────────────────────────────────────────────────────
        // Collect all schema names from base + extensions.
        const schemas = comp.schemas || {}
        const schemaNames = new Set([...Object.keys(schemas)])
        for (const ext of Object.values(extensions)) {
            if (!ext) continue
            for (const s of Object.keys(ext.schemas || {})) schemaNames.add(s)
        }

        for (const schema of schemaNames) {
            const key = schema === 'default' ? ref : `${ref}.${schema}`
            const baseFields = schemas[schema] && schemas[schema].fields

            // Collect extension fields for this schema.
            const extParts = []
            for (const [extName, ext] of Object.entries(extensions)) {
                if (!ext) continue
                const extSchemaFields = ext.schemas && ext.schemas[schema] && ext.schemas[schema].fields
                if (extSchemaFields && Object.keys(extSchemaFields).length > 0) {
                    extParts.push(`${extName}: ${objectShape(extSchemaFields)}`)
                }
            }

            const parts = []
            if (baseFields && Object.keys(baseFields).length > 0) {
                const baseProps = Object.entries(baseFields).flatMap(([k, node]) => {
                    const opt = node.required ? '' : '?'
                    if (node.type === 'relation') {
                        const itemTs = node.items ? idlTypeNodeToTs(node.items) : 'any'
                        const refType = node.cardinality === 'many' ? `Array<${itemTs}>` : itemTs
                        return [
                            `${k}${opt}: string`,
                            `${k}_RefObj?: ${refType}`
                        ]
                    }
                    return [`${k}${opt}: ${idlTypeNodeToTs(node)}`]
                })
                parts.push(...baseProps)
            }
            if (extParts.length > 0) {
                parts.push(`extensions: { ${extParts.join('; ')} }`)
            }

            if (parts.length === 0) {
                modelEntries.push(`  ${JSON.stringify(key)}: Record<string, any>`)
            } else {
                modelEntries.push(`  ${JSON.stringify(key)}: { ${parts.join('; ')} }`)
            }
        }

        // ── ComponentFunctions ───────────────────────────────────────────────
        // Merge base functions + all active extension functions under the bare ref.
        const allFunctions = [
            ...(comp.functions || []),
            ...Object.values(extensions).flatMap(ext => (ext && ext.functions) || []),
        ]
        if (allFunctions.length > 0) {
            const fnLines = allFunctions
                .map(fn => {
                    const parts = []
                    if (fn.input && Object.keys(fn.input).length > 0)
                        parts.push(`input: ${objectShape(fn.input)}`)
                    if (fn.output)
                        parts.push(`output: ${idlTypeNodeToTs(fn.output)}`)
                    if (parts.length === 0) return null
                    return `    ${JSON.stringify(fn.name)}: { ${parts.join('; ')} }`
                })
                .filter(Boolean)
            if (fnLines.length > 0)
                fnEntries.push(`  ${JSON.stringify(ref)}: {\n${fnLines.join('\n')}\n  }`)
        }
    }

    const modelsDts = modelEntries.length > 0
        ? `declare interface ComponentModels {\n${modelEntries.join('\n')}\n  [key: string]: any\n}`
        : ''
    const functionsDts = fnEntries.length > 0
        ? `declare interface ComponentFunctions {\n${fnEntries.join('\n')}\n  [key: string]: any\n}`
        : ''
    const typeLines = typesBlockLines(allTypes)
    const typesDts = typeLines.length > 0 ? typeLines.join('\n') : ''

    return { modelsDts, functionsDts, typesDts }
}

export function platformFunctionsToDts(functions) {
    if (!functions || functions.length === 0) return ''
    const entries = functions
        .map(fn => {
            const ts = signatureToTs(fn.signature)
            if (!ts) return null
            return `  "${fn.name}": ${ts}`
        })
        .filter(Boolean)
        .join('\n')
    if (!entries) return ''
    return `interface PlatformFunctions {\n${entries}\n}\n`
}

/**
 * Patches a protokol-sdk.d.ts string by replacing the empty
 * PlatformFunctions interface with one populated from the
 * provided function list. Returns the patched string.
 *
 * This avoids relying on TypeScript declaration-merging across separate
 * addExtraLib virtual files (which Monaco doesn't reliably handle).
 *
 * @param {string}  dtsText   Original .d.ts content
 * @param {Array<{ name: string, signature: Object|null }>} functions
 * @returns {string}
 */
export function patchPlatformFunction(dtsText, functions) {
    const body = platformFunctionsToDts(functions)
    // Build the replacement interface (always emit at least an empty body)
    const populated = body
        ? body.trim()   // already an interface block
        : 'interface PlatformFunctions {}'
    return dtsText.replace(
        /(?:declare\s+)?interface PlatformFunctions\s*\{[^}]*\}/,
        populated
    )
}

/**
 * Registers all platform function type overloads into a Monaco instance by
 * merging into the global PlatformFunctions interface.
 *
 * functions: Array<{ name: string, signature: Object|null }>
 *   — caller picks the right version (dev/live) before passing in.
 *
 * @param {Object} monaco  The Monaco editor instance
 * @param {Array}  functions
 */
export function registerAllPlatformFunctions(monaco, functions) {
    const dts = platformFunctionsToDts(functions)
    if (!dts) return
    const path = 'file:///ptkl-idl/platform-functions.d.ts'
    monaco.languages.typescript.javascriptDefaults.addExtraLib(dts, path)
    monaco.languages.typescript.typescriptDefaults.addExtraLib(dts, path)
}

/**
 * Generates a TypeScript declaration snippet that overrides the `input`
 * variable type inside a platform function expression editor.
 *
 * The IDL input signature maps to the request body; query and headers are
 * always present alongside it.
 *
 * @param {Object|null} signature  PlatformFunctionSignature
 * @returns {string}
 */
export function functionScopeToDts(signature) {
    const headerBase = `query: string; headers: Record<string, string>`
    if (!signature || !signature.input || Object.keys(signature.input).length === 0) {
        return `declare const input: { body: Record<string, any>; ${headerBase} };\n`
    }
    const bodyType = objectShape(signature.input)
    return `declare const input: { body: ${bodyType}; ${headerBase} };\n`
}

/**
 * Generates a TypeScript declaration snippet for a component function
 * expression editor.
 *
 * Component functions receive `input` directly — the payload object — NOT
 * wrapped in { body, query, headers } like platform functions.
 *
 * If the IDL input matches the platform function signature format (has a
 * `body` field alongside `query` / `headers`), we unwrap `body` and use its
 * type as the component function's `input` type. This is correct because at
 * runtime the component function receives the body payload directly.
 *
 * @param {Object|null} idlInput  IDL function input map (e.g. { name: IDLTypeNode, ... })
 * @returns {string}
 */
export function componentFunctionScopeToDts(idlInput) {
    if (!idlInput || Object.keys(idlInput).length === 0) {
        return `declare const input: any;\n`
    }
    // Detect when the IDL input was stored in platform HTTP-envelope format:
    // { body: IDLTypeNode, query?: IDLTypeNode, headers?: IDLTypeNode }.
    // Component functions receive the body payload directly — they have no
    // HTTP envelope. We unwrap `body` and use its type as `input`.
    //
    // Detection: body is present as an IDLTypeNode (has a `.type` property)
    // AND every key in the input map is one of the known envelope keys.
    const ENVELOPE_KEYS = ['body', 'query', 'headers']
    const keys = Object.keys(idlInput)
    const isEnvelope =
        idlInput.body &&
        typeof idlInput.body === 'object' &&
        'type' in idlInput.body &&
        keys.every(k => ENVELOPE_KEYS.includes(k))
    if (isEnvelope) {
        const bodyType = idlTypeNodeToTs(idlInput.body)
        return `declare const input: ${bodyType};\n`
    }
    return `declare const input: ${objectShape(idlInput)};\n`
}

/**
 * Generates concrete per-ref interface declarations and constructor/method
 * overloads so Monaco hover displays fully-resolved types.
 *
 * Monaco's TypeScript quickinfo resolves generic types in parameter positions
 * but NOT in return-type positions. By generating concrete interfaces for
 * each known component ref (with inline field shapes instead of
 * ComponentModels[C]) and constructor overloads that return these interfaces,
 * we sidestep the limitation entirely.
 *
 * @param {Object} components  Record<string, ComponentIDLEntry>
 * @param {Array<{ name: string, signature: Object|null }>} functions  Platform functions
 * @returns {{
 *   interfacesDts: string,
 *   ctorOverloads: string,
 *   platformOverloads: string,
 *   runOverloads: string
 * }}
 */
export function generateConcreteTypes(components, functions) {
    const interfaces = []
    const ctorOverloads = []
    const platformOverloads = []
    const runOverloads = []

    // ── Per-component concrete interfaces ────────────────────────────────
    if (components) {
        for (const [ref, comp] of Object.entries(components)) {
            if (!comp) continue

            // Merge all functions (base + extensions) — shared across all schemas.
            const allFunctions = [
                ...(comp.functions || []),
                ...Object.values(comp.extensions || {}).flatMap(ext => (ext && ext.functions) || []),
            ]

            // Helper: emit a concrete interface + overloads for one ref key.
            const emitEntry = (key, fieldsTs) => {
                const safeName = key.replace(/[^a-zA-Z0-9_]/g, '_')
                const jsonKey = JSON.stringify(key)

                const fnLines = []
                for (const fn of allFunctions) {
                    const inputType = (fn.input && Object.keys(fn.input).length > 0)
                        ? `Partial<${objectShape(fn.input)}>`
                        : 'Record<string, any>'
                    const outputType = fn.output ? idlTypeNodeToTs(fn.output) : 'any'
                    fnLines.push(`  function(name: ${JSON.stringify(fn.name)}, input?: ${inputType}): Promise<${outputType}>;`)
                }
                fnLines.push('  function(name: string, input?: Record<string, any>): Promise<any>;')

                interfaces.push(
                    `declare interface _C_${safeName} {\n` +
                    `  find(filters: FindParams, opts?: FindOptions): Promise<{ data: { items: Array<${fieldsTs}>; total: number; page: number; pages: number } }>;\n` +
                    `  findOne(filter: FindAdvancedParams, opts?: FindOptions): Promise<${fieldsTs} | null>;\n` +
                    `  get(uuid: string): Promise<{ data: ${fieldsTs} }>;\n` +
                    `  create(model: Partial<${fieldsTs}>): Promise<any>;\n` +
                    `  update(uuid: string, data: Partial<${fieldsTs}>, options?: UpdateOptions): Promise<any>;\n` +
                    `  updateMany(data: Array<Partial<${fieldsTs}>>, options?: UpdateManyOptions): Promise<any>;\n` +
                    `  modify(filters: Record<string, any>, data: Partial<${fieldsTs}>, options?: ModifyOptions): Promise<any>;\n` +
                    `  concurrentUpdate(uuid: string, version: number, data: Partial<${fieldsTs}>, options?: UpdateOptions): Promise<any>;\n` +
                    `  delete(uuid: string): Promise<any>;\n` +
                    `  aggregate(pipeline: FindAggregateParams): AggregateChainable;\n` +
                    `  settings(): Promise<any>;\n` +
                    `  saveSettings(settings: any, version: string): Promise<any>;\n` +
                    `  saveTemplatesDist(version: string, sdkVersion: string, engine: string, dist: Record<string, string>): Promise<any>;\n` +
                    `  workflow(event: string, input: any): Promise<any>;\n` +
                    fnLines.join('\n') + '\n' +
                    `  revisions(uuid: string): Promise<any>;\n` +
                    `}`
                )

                ctorOverloads.push(`    new(ref: ${jsonKey}, options?: ComponentOptions): _C_${safeName};`)
                platformOverloads.push(`  component(ref: ${jsonKey}, options?: ComponentOptions): _C_${safeName};`)
            }

            // Collect all schema names from base + extensions.
            const compExtensions = comp.extensions || {}
            const schemaNames = new Set([...Object.keys(comp.schemas || {})])
            for (const ext of Object.values(compExtensions)) {
                if (!ext) continue
                for (const s of Object.keys(ext.schemas || {})) schemaNames.add(s)
            }

            for (const schema of schemaNames) {
                const key = schema === 'default' ? ref : `${ref}.${schema}`
                const baseSchemaIDL = (comp.schemas || {})[schema]
                const baseFields = baseSchemaIDL && baseSchemaIDL.fields

                // Collect extension fields for this schema.
                const extParts = []
                for (const [extName, ext] of Object.entries(compExtensions)) {
                    if (!ext) continue
                    const extSchemaFields = ext.schemas && ext.schemas[schema] && ext.schemas[schema].fields
                    if (extSchemaFields && Object.keys(extSchemaFields).length > 0) {
                        extParts.push(`${extName}: ${objectShape(extSchemaFields)}`)
                    }
                }

                const parts = []
                if (baseFields && Object.keys(baseFields).length > 0) {
                    const baseProps = Object.entries(baseFields).flatMap(([k, node]) => {
                        const opt = node.required ? '' : '?'
                        if (node.type === 'relation') {
                            const itemTs = node.items ? idlTypeNodeToTs(node.items) : 'any'
                            const refType = node.cardinality === 'many' ? `Array<${itemTs}>` : itemTs
                            return [
                                `${k}${opt}: string`,
                                `${k}_RefObj?: ${refType}`
                            ]
                        }
                        return [`${k}${opt}: ${idlTypeNodeToTs(node)}`]
                    })
                    parts.push(...baseProps)
                }
                if (extParts.length > 0) {
                    parts.push(`extensions: { ${extParts.join('; ')} }`)
                }

                const fieldsTs = parts.length > 0
                    ? `{ ${parts.join('; ')} }`
                    : 'Record<string, any>'
                emitEntry(key, fieldsTs)
            }
        }
    }

    // ── Per-function concrete run() overloads ────────────────────────────
    if (functions) {
        for (const fn of functions) {
            if (!fn.signature) continue
            const inputType = (fn.signature.input && Object.keys(fn.signature.input).length > 0)
                ? `Partial<${objectShape(fn.signature.input)}>`
                : 'Record<string, any>'
            const outputType = fn.signature.output ? idlTypeNodeToTs(fn.signature.output) : 'any'
            runOverloads.push(
                `  run(ref: ${JSON.stringify(fn.name)}, d: { input?: ${inputType}; query?: Record<string, string>; headers?: Record<string, string> }): Promise<${outputType}>;`
            )
        }
    }

    return {
        interfacesDts: interfaces.join('\n\n'),
        ctorOverloads: ctorOverloads.join('\n'),
        platformOverloads: platformOverloads.join('\n'),
        runOverloads: runOverloads.join('\n')
    }
}
